import axios from "axios";
import React, { useState, useCallback, Component } from "react";
import { GoogleLogin, GoogleOAuthProvider } from "@react-oauth/google";
import { Image } from "semantic-ui-react";
import "../css/home.css";
import "../css/index.css";
import Constants from "../util/Constants";
import Footer from "./Footer";
import Home from "./Home";
import { AboutPage, TermsPage, PrivacyPage } from "./LegalPages";
import SubmitFeature from "./SubmitFeature";
import jwtDecode from "jwt-decode";
import IndexCharts from "./IndexCharts";
import MainNews from "./MainNews";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Grid from "@mui/material/Grid";
import { styled } from "@mui/material/styles";
import AssessmentIcon from "@mui/icons-material/Assessment";
import FilterListIcon from "@mui/icons-material/FilterList";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import NewspaperIcon from "@mui/icons-material/Newspaper";
import TimelineIcon from "@mui/icons-material/Timeline";
import DashboardIcon from "@mui/icons-material/Dashboard";

export const UserContext = React.createContext(null);

class LoginErrorBoundary extends Component {
  state = { error: null };
  static getDerivedStateFromError(error) {
    return { error };
  }
  componentDidCatch(error, info) {
    console.error("Post-login error (check Netlify deploy):", error, info);
  }
  render() {
    if (this.state.error) {
      return (
        <Box sx={{ p: 3, textAlign: "center", maxWidth: 480, margin: "2rem auto" }}>
          <Typography color="error" gutterBottom>
            Something went wrong after login
          </Typography>
          <Typography variant="body2" sx={{ fontFamily: "monospace", wordBreak: "break-all" }}>
            {this.state.error?.message || String(this.state.error)}
          </Typography>
          <Typography variant="caption" display="block" sx={{ mt: 2 }}>
            Check the browser console for details.
          </Typography>
        </Box>
      );
    }
    return this.props.children;
  }
}

// Modern slick styled components
const StyledHeader = styled(Paper)(({ theme }) => ({
  backgroundColor: "#0d1b2a",
  padding: "0.5rem 0.75rem",
  display: "flex",
  flexWrap: "wrap",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "0.5rem",
  borderRadius: 0,
  boxShadow: "0 4px 24px rgba(0,0,0,0.12)",
  backdropFilter: "blur(10px)",
  [theme.breakpoints.up("sm")]: {
    padding: "0.5rem 1rem",
    flexWrap: "nowrap",
  },
}));

const HeroSection = styled(Paper)(({ theme }) => ({
  padding: "1.5rem 1rem",
  textAlign: "center",
  background: "linear-gradient(145deg, #ffffff 0%, #f0f4f8 50%, #e8eef5 100%)",
  borderRadius: "16px",
  margin: "0.5rem 0",
  width: "100%",
  maxWidth: "100%",
  boxSizing: "border-box",
  boxShadow: "0 8px 32px rgba(16, 33, 56, 0.08), 0 2px 8px rgba(0,0,0,0.04)",
  border: "1px solid rgba(255,255,255,0.8)",
  position: "relative",
  overflow: "hidden",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "4px",
    background: "linear-gradient(90deg, #d4a017 0%, #e8c547 50%, #d4a017 100%)",
    opacity: 0.9,
  },
  [theme.breakpoints.up("sm")]: {
    padding: "2.5rem 3rem",
    margin: "1rem 0",
  },
}));

const IntroText = styled(Typography)(({ theme }) => ({
  color: "#4a5568",
  fontSize: "0.95rem",
  lineHeight: 1.7,
  maxWidth: "720px",
  margin: "1.25rem auto",
  fontWeight: 400,
  letterSpacing: "0.01em",
  [theme.breakpoints.up("sm")]: {
    fontSize: "1.05rem",
  },
}));

const TrustBar = styled(Box)(({ theme }) => ({
  display: "flex",
  flexWrap: "wrap",
  justifyContent: "center",
  gap: "0.5rem 1rem",
  padding: "0.75rem 1rem",
  background: "linear-gradient(135deg, rgba(16, 33, 56, 0.03) 0%, rgba(212, 160, 23, 0.04) 100%)",
  borderRadius: "999px",
  marginTop: "1.5rem",
  border: "1px solid rgba(16, 33, 56, 0.06)",
  boxShadow: "inset 0 1px 0 rgba(255,255,255,0.5)",
  "& span": {
    color: "#5a6c7d",
    fontSize: "0.8rem",
    fontWeight: 600,
    letterSpacing: "0.02em",
  },
  [theme.breakpoints.up("sm")]: {
    gap: "0.75rem 1.5rem",
    padding: "0.875rem 1.5rem",
    marginTop: "1.75rem",
    "& span": { fontSize: "0.875rem" },
  },
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  color: "#0d1b2a",
  fontWeight: 700,
  fontSize: "1.1rem",
  marginBottom: "1.5rem",
  textAlign: "center",
  letterSpacing: "-0.02em",
  [theme.breakpoints.up("sm")]: {
    fontSize: "1.25rem",
  },
  [theme.breakpoints.up("md")]: {
    fontSize: "1.5rem",
  },
}));

const FeatureCard = styled(Paper)(({ theme }) => ({
  padding: "1.5rem 1.25rem",
  height: "100%",
  minHeight: "180px",
  display: "flex",
  flexDirection: "column",
  borderRadius: "16px",
  border: "1px solid rgba(16, 33, 56, 0.06)",
  background: "linear-gradient(180deg, #ffffff 0%, #fafbfc 100%)",
  boxShadow: "0 4px 20px rgba(0,0,0,0.04), 0 1px 3px rgba(0,0,0,0.02)",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  "&:hover": {
    transform: "translateY(-4px)",
    boxShadow: "0 12px 40px rgba(16, 33, 56, 0.12), 0 4px 12px rgba(0,0,0,0.06)",
    borderColor: "rgba(212, 160, 23, 0.2)",
  },
}));

const ExploreCard = styled(Paper)(({ theme }) => ({
  padding: "1rem 1.25rem",
  borderRadius: "14px",
  cursor: "pointer",
  border: "1px solid rgba(16, 33, 56, 0.08)",
  background: "linear-gradient(180deg, #ffffff 0%, #fafbfc 100%)",
  boxShadow: "0 4px 16px rgba(0,0,0,0.04)",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  display: "flex",
  alignItems: "center",
  gap: "1rem",
  minHeight: "44px",
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: "0 8px 24px rgba(16, 33, 56, 0.1)",
    borderColor: "rgba(212, 160, 23, 0.3)",
  },
  [theme.breakpoints.up("sm")]: {
    padding: "1.25rem 1.5rem",
  },
}));

function MainHome() {
  const [showMain, setShowMain] = useState(true);
  const [showAccessRequest, setShowAccessRequest] = useState(false);
  const [screenToRender, setScreenToRender] = useState(null);
  const [userEmail, setUserEmail] = useState(null);
  const [loggedInUser, setLoggedInUser] = useState(null);
  const [activePage, setActivePage] = useState(null);
  const [showNews, setShowNews] = useState(false);
  const [showMarkets, setShowMarkets] = useState(false);
  const [showFeatures, setShowFeatures] = useState(false);

  const handleGoogleSignin = useCallback(async (googleUser) => {
    const decodedCreds = jwtDecode(googleUser.credential);
    const userName = decodedCreds.name;
    const email = decodedCreds.email;

    try {
      const response = await axios.get(
        `${Constants.VALIDATE_URL}${email}?name=${userName}`
      );
      if (response.data?.user) {
        const u = response.data.user;
        setUserEmail(u.email);
        setShowMain(false);
        setShowAccessRequest(false);
        setLoggedInUser({
          userName: (u.name && String(u.name).split(" ")[0]) || userName || "User",
          userEmail: u.email,
          newUser: !!u.newUser,
        });
      } else {
        setShowAccessRequest(true);
        setShowMain(false);
        setScreenToRender(
          <SubmitFeature
            userName={userName}
            email={email}
            contactURL={Constants.ACCESS_REQUEST_URL}
            header="It appears you are not setup in the website yet. Don't feel bad. Fill out the form to request access."
          />
        );
      }
    } catch (error) {
      console.error("Error fetching user data:", error);
    }
  }, []);

  const handleTempSignin = useCallback(() => {
    setLoggedInUser({ userName: "SimplyStock" });
  }, []);

  const tempSigninButton = window.location.hostname === "localhost" && (
    <Box
      component="button"
      onClick={handleTempSignin}
      sx={{
        padding: "0.5rem 1rem",
        height: "40px",
        display: "flex",
        alignItems: "center",
        borderRadius: "8px",
        border: "1px solid rgba(212, 160, 23, 0.5)",
        background: "linear-gradient(135deg, rgba(212, 160, 23, 0.15) 0%, rgba(212, 160, 23, 0.08) 100%)",
        color: "#0d1b2a",
        fontWeight: 600,
        fontSize: "0.875rem",
        cursor: "pointer",
        fontFamily: "inherit",
        transition: "all 0.2s ease",
        "&:hover": {
          background: "linear-gradient(135deg, rgba(212, 160, 23, 0.25) 0%, rgba(212, 160, 23, 0.15) 100%)",
          borderColor: "rgba(212, 160, 23, 0.8)",
          transform: "translateY(-1px)",
        },
      }}
    >
      Temp Login
    </Box>
  );

  if (loggedInUser) {
    return (
      <LoginErrorBoundary>
        <UserContext.Provider value={loggedInUser.userEmail ?? null}>
          <Home
            userName={loggedInUser.userName}
            userEmail={loggedInUser.userEmail}
            newUser={loggedInUser.newUser}
          />
        </UserContext.Provider>
      </LoginErrorBoundary>
    );
  }

  return (
    <GoogleOAuthProvider clientId={Constants.GOOGLE_CLIENT_ID}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          minHeight: "100vh",
          overflowX: "hidden",
          background: "linear-gradient(180deg, #f5f7fa 0%, #eef1f5 50%, #f8f9fa 100%)",
          backgroundAttachment: "fixed",
        }}
      >
        {/* Header */}
        <StyledHeader elevation={0}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "0.25rem",
              backgroundColor: "transparent",
              cursor: activePage ? "pointer" : "default",
            }}
            onClick={activePage ? () => setActivePage(null) : undefined}
          >
            <Image
              size="medium"
              src={`${process.env.PUBLIC_URL}/images/simply.png`}
              style={{
                width: "auto",
                height: "clamp(2rem, 5vw, 2.75rem)",
                borderRadius: "8px",
                border: "2px solid #d4a017",
              }}
            />
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: { xs: "0.5rem", sm: "1rem" }, flexShrink: 0 }}>
            {tempSigninButton}
            <GoogleLogin
              type="standard"
              theme="filled_black"
              size="medium"
              text="signin_with"
              onSuccess={handleGoogleSignin}
              onFailure={() => {}}
              useOneTap
              cookiePolicy={"single_host_origin"}
            />
          </Box>
        </StyledHeader>

        {/* Main Content */}
        <Box
          sx={{
            flex: 1,
            padding: { xs: "0 0.75rem", sm: 0 },
            maxWidth: "1400px",
            margin: "0 auto",
            width: "100%",
            boxSizing: "border-box",
          }}
        >
          {activePage === "about" && (
            <AboutPage onBack={() => setActivePage(null)} />
          )}
          {activePage === "terms" && (
            <TermsPage onBack={() => setActivePage(null)} />
          )}
          {activePage === "privacy" && (
            <PrivacyPage onBack={() => setActivePage(null)} />
          )}
          {!activePage && (
            <>
              <HeroSection elevation={0}>
                <Typography
                  variant="h3"
                  gutterBottom
                  sx={{
                    color: "#0d1b2a",
                    fontWeight: 800,
                    fontSize: { xs: "1.5rem", sm: "2rem", md: "2.5rem" },
                    letterSpacing: "-0.03em",
                    marginBottom: "0.75rem",
                    marginTop: 0,
                    lineHeight: 1.2,
                  }}
                >
                  Stock analysis done faster, easier and simpler!
                </Typography>
                <IntroText>
                  Screen thousands of stocks, explore curated investment ideas,
                  and analyze companies with fundamental data and valuation
                  tools. SimplyStock gives you the research platform to make
                  better investment decisions.
                </IntroText>
                <TrustBar>
                  <span>✓ Screening thousands of U.S. stocks</span>
                  <span>✓ Data from trusted providers</span>
                  <span>✓ Free to use</span>
                </TrustBar>
                <Box
                  sx={{
                    marginTop: "1.5rem",
                    padding: { xs: "1.25rem 1rem", sm: "1.5rem 2rem" },
                    background: "linear-gradient(135deg, rgba(13, 27, 42, 0.03) 0%, rgba(212, 160, 23, 0.06) 100%)",
                    borderRadius: "14px",
                    border: "1px solid rgba(212, 160, 23, 0.25)",
                    boxShadow: "0 4px 20px rgba(13, 27, 42, 0.06)",
                  }}
                >
                  <Typography sx={{ color: "#0d1b2a", fontSize: "1rem", fontWeight: 700, marginBottom: "1rem", textAlign: "center" }}>
                    Sign in to get started
                  </Typography>
                  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "0.75rem" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: "1rem", flexWrap: "wrap", justifyContent: "center" }}>
                      {tempSigninButton}
                      <GoogleLogin
                        type="standard"
                        theme="filled_black"
                        size="large"
                        text="signin_with"
                        onSuccess={handleGoogleSignin}
                        onFailure={() => {}}
                        useOneTap
                        cookiePolicy={"single_host_origin"}
                      />
                    </Box>
                    <Typography sx={{ color: "#6b7c8d", fontSize: "0.875rem", fontWeight: 500 }}>
                      No credit card required • Free to use
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: "1rem", justifyContent: "center", marginTop: "1.5rem" }}>
                  <ExploreCard elevation={0} onClick={() => { if (showNews) { setShowNews(false); } else { setShowNews(true); setShowMarkets(false); setShowFeatures(false); } }}>
                    <Box sx={{ width: 44, height: 44, borderRadius: "10px", background: "linear-gradient(135deg, #0d1b2a 0%, #1b3a4b 100%)", color: "white", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <NewspaperIcon sx={{ fontSize: 22 }} />
                    </Box>
                    <Box sx={{ textAlign: "left" }}>
                      <Typography sx={{ fontWeight: 700, color: "#0d1b2a", fontSize: "1rem" }}>Explore News</Typography>
                      <Typography sx={{ color: "#6b7c8d", fontSize: "0.85rem", mt: 0.25 }}>Latest market news and headlines</Typography>
                    </Box>
                  </ExploreCard>
                  <ExploreCard elevation={0} onClick={() => { if (showMarkets) { setShowMarkets(false); } else { setShowMarkets(true); setShowNews(false); setShowFeatures(false); } }}>
                    <Box sx={{ width: 44, height: 44, borderRadius: "10px", background: "linear-gradient(135deg, #0d1b2a 0%, #1b3a4b 100%)", color: "white", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <TimelineIcon sx={{ fontSize: 22 }} />
                    </Box>
                    <Box sx={{ textAlign: "left" }}>
                      <Typography sx={{ fontWeight: 700, color: "#0d1b2a", fontSize: "1rem" }}>Explore Markets</Typography>
                      <Typography sx={{ color: "#6b7c8d", fontSize: "0.85rem", mt: 0.25 }}>Dow, S&P 500, Nasdaq index charts</Typography>
                    </Box>
                  </ExploreCard>
                  <ExploreCard elevation={0} onClick={() => { if (showFeatures) { setShowFeatures(false); } else { setShowFeatures(true); setShowNews(false); setShowMarkets(false); } }}>
                    <Box sx={{ width: 44, height: 44, borderRadius: "10px", background: "linear-gradient(135deg, #0d1b2a 0%, #1b3a4b 100%)", color: "white", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <DashboardIcon sx={{ fontSize: 22 }} />
                    </Box>
                    <Box sx={{ textAlign: "left" }}>
                      <Typography sx={{ fontWeight: 700, color: "#0d1b2a", fontSize: "1rem" }}>Explore Features</Typography>
                      <Typography sx={{ color: "#6b7c8d", fontSize: "0.85rem", mt: 0.25 }}>Screeners, analysis, portfolios & more</Typography>
                    </Box>
                  </ExploreCard>
                </Box>
              </HeroSection>

              {/* News - shown when user clicks Explore News */}
              {showNews && (
                <Box
                  sx={{
                    marginTop: "2rem",
                    padding: { xs: "0 0.75rem", sm: 0 },
                    maxWidth: "1200px",
                    marginLeft: "auto",
                    marginRight: "auto",
                    width: "100%",
                  }}
                >
                  <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, alignItems: { xs: "stretch", sm: "center" }, justifyContent: "space-between", gap: 0.5, mb: 1.5 }}>
                    <SectionTitle sx={{ marginBottom: 0, textAlign: "left" }}>Latest News</SectionTitle>
                    <Typography component="button" onClick={() => setShowNews(false)} sx={{ color: "#6b7c8d", fontSize: "0.9rem", cursor: "pointer", background: "none", border: "none", fontWeight: 500, alignSelf: { xs: "flex-end", sm: "auto" }, minHeight: "44px", display: "flex", alignItems: "center", "&:hover": { color: "#0d1b2a" } }}>
                      Collapse ↑
                    </Typography>
                  </Box>
                  <MainNews isNewsOpen={true} />
                </Box>
              )}

              {/* Markets - shown when user clicks Explore Markets */}
              {showMarkets && (
                <Box
                  sx={{
                    marginTop: "2rem",
                    padding: { xs: "0 0.75rem", sm: 0 },
                    maxWidth: "1200px",
                    marginLeft: "auto",
                    marginRight: "auto",
                    width: "100%",
                  }}
                >
                  <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, alignItems: { xs: "stretch", sm: "center" }, justifyContent: "space-between", gap: 0.5, mb: 1.5 }}>
                    <SectionTitle sx={{ marginBottom: 0, textAlign: "left" }}>Market Indexes</SectionTitle>
                    <Typography component="button" onClick={() => setShowMarkets(false)} sx={{ color: "#6b7c8d", fontSize: "0.9rem", cursor: "pointer", background: "none", border: "none", fontWeight: 500, alignSelf: { xs: "flex-end", sm: "auto" }, minHeight: "44px", display: "flex", alignItems: "center", "&:hover": { color: "#0d1b2a" } }}>
                      Collapse ↑
                    </Typography>
                  </Box>
                  <IndexCharts />
                </Box>
              )}

              {/* Features - shown when user clicks Explore Features */}
              {showFeatures && (
                <Box
                  sx={{
                    marginTop: "2rem",
                    padding: { xs: "1.5rem 1rem", sm: "2rem 1.5rem" },
                    maxWidth: "1200px",
                    marginLeft: "auto",
                    marginRight: "auto",
                    width: "100%",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: { xs: "column", sm: "row" },
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 1,
                      marginBottom: "1.75rem",
                      padding: 0,
                    }}
                  >
                    <SectionTitle sx={{ marginBottom: 0, textAlign: "left" }}>What You Get</SectionTitle>
                    <Typography
                      component="button"
                      onClick={() => setShowFeatures(false)}
                      sx={{
                        color: "#6b7c8d",
                        fontSize: "0.9rem",
                        cursor: "pointer",
                        background: "none",
                        border: "none",
                        fontWeight: 500,
                        flexShrink: 0,
                        "&:hover": { color: "#0d1b2a" },
                      }}
                    >
                      Collapse ↑
                    </Typography>
                  </Box>
                  <Grid container spacing={{ xs: 2, sm: 2.5, md: 3 }} sx={{ width: "100%", mx: 0 }}>
                    <Grid item xs={12} sm={6} md={6} lg={3}>
                      <FeatureCard elevation={0}>
                        <Box sx={{ width: 48, height: 48, borderRadius: "12px", background: "linear-gradient(135deg, #d4a017 0%, #e8c547 100%)", color: "white", display: "flex", alignItems: "center", justifyContent: "center", mb: 1.5, boxShadow: "0 4px 12px rgba(212, 160, 23, 0.3)" }}>
                          <AssessmentIcon sx={{ fontSize: 24 }} />
                        </Box>
                        <Typography sx={{ fontWeight: 600, color: "#102138", fontSize: "1rem", mb: 0.5 }}>
                          Fundamental Analysis
                        </Typography>
                        <Typography sx={{ color: "#5a6c7d", fontSize: "0.9rem", lineHeight: 1.55, flex: 1 }}>
                          Company profiles, valuations, and intrinsic value estimates
                        </Typography>
                      </FeatureCard>
                    </Grid>
                    <Grid item xs={12} sm={6} md={6} lg={3}>
                      <FeatureCard elevation={0}>
                        <Box sx={{ width: 48, height: 48, borderRadius: "12px", background: "linear-gradient(135deg, #d4a017 0%, #e8c547 100%)", color: "white", display: "flex", alignItems: "center", justifyContent: "center", mb: 1.5, boxShadow: "0 4px 12px rgba(212, 160, 23, 0.3)" }}>
                          <FilterListIcon sx={{ fontSize: 24 }} />
                        </Box>
                        <Typography sx={{ fontWeight: 600, color: "#102138", fontSize: "1rem", mb: 0.5 }}>
                          Stock Screeners
                        </Typography>
                        <Typography sx={{ color: "#5a6c7d", fontSize: "0.9rem", lineHeight: 1.55, flex: 1 }}>
                          Filter by growth, value, quality, and custom criteria
                        </Typography>
                      </FeatureCard>
                    </Grid>
                    <Grid item xs={12} sm={6} md={6} lg={3}>
                      <FeatureCard elevation={0}>
                        <Box sx={{ width: 48, height: 48, borderRadius: "12px", background: "linear-gradient(135deg, #d4a017 0%, #e8c547 100%)", color: "white", display: "flex", alignItems: "center", justifyContent: "center", mb: 1.5, boxShadow: "0 4px 12px rgba(212, 160, 23, 0.3)" }}>
                          <ShowChartIcon sx={{ fontSize: 24 }} />
                        </Box>
                        <Typography sx={{ fontWeight: 600, color: "#102138", fontSize: "1rem", mb: 0.5 }}>
                          Portfolio Tools
                        </Typography>
                        <Typography sx={{ color: "#5a6c7d", fontSize: "0.9rem", lineHeight: 1.55, flex: 1 }}>
                          Track performance and monitor your investments
                        </Typography>
                      </FeatureCard>
                    </Grid>
                    <Grid item xs={12} sm={6} md={6} lg={3}>
                      <FeatureCard elevation={0}>
                        <Box sx={{ width: 48, height: 48, borderRadius: "12px", background: "linear-gradient(135deg, #d4a017 0%, #e8c547 100%)", color: "white", display: "flex", alignItems: "center", justifyContent: "center", mb: 1.5, boxShadow: "0 4px 12px rgba(212, 160, 23, 0.3)" }}>
                          <LightbulbIcon sx={{ fontSize: 24 }} />
                        </Box>
                        <Typography sx={{ fontWeight: 600, color: "#102138", fontSize: "1rem", mb: 0.5 }}>
                          Curated Ideas
                        </Typography>
                        <Typography sx={{ color: "#5a6c7d", fontSize: "0.9rem", lineHeight: 1.55, flex: 1 }}>
                          Investment themes and research-backed stock lists
                        </Typography>
                      </FeatureCard>
                    </Grid>
                  </Grid>
                </Box>
              )}
            </>
          )}
        </Box>

        {/* Footer */}
        <Footer
          onNavigate={setActivePage}
          style={{
            flexShrink: 0,
            width: "100%",
            backgroundColor: "#ffffff",
            borderTop: "1px solid #eaeaea",
          }}
        />
      </Box>
    </GoogleOAuthProvider>
  );
}

export default MainHome;
